import { useMemo } from "react";
import { Chart } from "../../../../common/components/Chart/Chart";
import { DropdownMenu } from "../../../../common/components/DropdownMenu";
import FullModal from "../../../../common/components/FullModal";
import { piezometriService } from "../../../../services/PiezometriService";
import { Table } from "../../components/micro/Table";
import { Tr } from "../../components/micro/Tr";
import { useEffect, useState } from "react";
import { downloadExcel } from "../../../../common/functions/downloadExcel";
import { SearchElement, SearchForm, SearchLabel, SearchRow } from "../../../../common/components/Search/Search";
import { targetValue } from "../../../../common/functions/targetValue";

/**
 * @typedef ConducibilitaPageProps
 * @prop {string} idPiezometro
 */

/**
 * @typedef MeasurementSearchParams
 * @prop {Date} from
 * @prop {Date} to
 */

/**
 * @typedef Measure
 * @prop {string} date
 * @prop {string} conductivity
 * @prop {string} so4
 */

const api = {
    /**
     * @param {string} idPiezometro
     * @param {MeasurementSearchParams} params
     * @returns {Promise<Measure[]>}
     */
    search: async (idPiezometro, params) =>
        (await piezometriService.getConductivity(
            idPiezometro,
            params.from,
            params.to
        )).map(toMeasure)
    ,

    last: async idPiezometro => toMeasure(
        await piezometriService.lastConductivity(idPiezometro)
    )
}

const toMeasure = c => c == null ? null : ({
    date: c.date.toLocaleDateString(),
    conductivity: c.ce,
    so4: c.so4
})

export function ConducibilitaPage({idPiezometro, nomePiezometro}) {

    const [lastMeasurement, setLastMeasurement] = useState()
    useEffect(() => {
        api.last(idPiezometro).then(setLastMeasurement)
    }, [idPiezometro])

    /**
     * @type {[Measure[], (m: Measure[]) => void]}
     */
    const [filteredData, setFilteredData] = useState([])
    const chartData = useMemo(() => filteredData.map(d => [
        new Date(d.date).getTime(),
        parseFloat(d.conductivity),
    ]), [filteredData])

    const [loading, setLoading] = useState(false)

    const onSubmit = (e) => {
        e.preventDefault()
        switch (getAction(e)) {
            case 'search':
                setLoading(true)
                api.search(idPiezometro, getSearchParams(e))
                    .then(setFilteredData)
                    .then(() => setLoading(false))
                    .then(() => setSearched(true))
                break
            case 'clear':
                e.target.reset()
                setSearched(false)
                setFilteredData([])
                break
            default: console.warn("unknown action:", getAction(e))
        }
    }

    const getAction = e => e.nativeEvent.submitter.name

    /**
     * @param {FormEvent} e
     * @returns {MeasurementSearchParams}
     */
    const getSearchParams = e => {
        const rawParams = Object.fromEntries(
            ['from', 'to'].map(el => [el, e.target.elements[el].value])
        )

        return {
            from: new Date(rawParams.from === '' ? 0 : rawParams.from),
            to: new Date(rawParams.to === '' ? '3000-12-31' : rawParams.to)
        }
    }

    const [showChart, setShowChart] = useState(false)
    const onChart = e => {
        e.preventDefault()
        setShowChart(true)
    }

    const onExport = e => {
        e.preventDefault()
        xlsxExport(`Piezometro ${nomePiezometro} - Conducibilità elettrica e SO4`, filteredData)
    }

    const [searched, setSearched] = useState(false)

    return (
        <>

            <SamplesTable
                title={<th colSpan='100%'>Ultima misura{lastMeasurement ? '' : ' non disponibile'}</th>}
                samples={lastMeasurement ? [lastMeasurement] : []}
            />


            <SearchForm className="p-3" onSubmit={onSubmit}>
                <SearchRow>
                    <SearchElement>
                        <SearchLabel>Da:&nbsp;</SearchLabel>
                        <input id="from" name="from" type="date"/>
                    </SearchElement>

                    <SearchElement>
                        <SearchLabel>A:&nbsp;</SearchLabel>
                        <input id="to" name="to" type="date"/>
                    </SearchElement>

                    <div className="d-flex align-items-end justify-content-end">
                        <button type="submit" name="search" id="search-btn" className="btn btn-sm"><i className="fa fa-search text-white"></i></button>
                        <button type="submit" name="clear" id="clear-btn" className="btn btn-sm"><i className="fa fa-eraser text-white"></i></button>
                        <DropdownMenu
                            className="text-dark"
                            trigger={<i className="fa fa-bars text-white"></i>}
                            items={[
                                {text: 'Grafico', onClick: onChart},
                                {text: 'Esporta', onClick: onExport}
                            ]}
                        />
                    </div>
                </SearchRow>
            </SearchForm>

            {loading
                ? <div className="text-center my-5">RICERCA IN CORSO...</div>
                : <SamplesTable
                    samples={filteredData}
                    noResults={searched
                        ? 'Nessun risultato'
                        : 'Effettua una ricerca per visualizzare dei dati'
                    } />
            }

            {showChart &&
                <FullModal onClose={() => setShowChart(false)}>
                    <Chart
                        title={nomePiezometro}
                        subtitle='Piezometro'
                        name='Conducibilità elettrica'
                        yTitle='µS/cm'
                        data={chartData}
                    />
                </FullModal>
            }
        </>
    )
}

/**
 * @typedef {SamplesTableProps}
 * @prop {Measure[]} samples
 * @prop {ReactNode | undefined} title
 * @prop {ReactNode | undefined} noResults
 */

/**
 * @param {SamplesTableProps} param0
 * @returns
 */
function SamplesTable({
    samples,
    title,
    noResults
}) {
    return (
        <Table>
            <thead>
                {title && <Tr className="title">{title}</Tr>}
                <Tr>
                    <th>Conducibilità elettrica [µS/cm]</th>
                    <th>SO4 [mg/l]</th>
                    <th>Data</th>
                </Tr>
            </thead>

            <tbody>
                {samples == null || samples.length === 0 && noResults &&
                    <Tr><td className="text-center" colSpan="3">{noResults}</td></Tr>
                }
                {samples.map((sample, i) =>
                    <Tr key={i}>
                        <td>{sample.conductivity}</td>
                        <td>{sample.so4}</td>
                        <td>{sample.date}</td>
                    </Tr>
                )}
            </tbody>
        </Table>
    )
}

/**
 * @param {string} title
 * @param {Measure[]} samples
 */
const xlsxExport = (title, samples) => {

    const data = [
        ['Conducibilità elettrica [µS/cm]', 'SO4 [mg/l]', 'Data'],
        ...samples.map(sample => [
            sample.conductivity,
            sample.so4,
            sample.date
        ])
    ]

    downloadExcel(title, data);
}